/**
 * Pure tab-strip state for the center pane. Kept free of React so the edge
 * cases (closing the active tab, reopening an open artifact, updating a title)
 * are unit-testable.
 */

import { artifactKey, type Artifact, type ArtifactKind } from "./types";

const KINDS: ReadonlySet<ArtifactKind> = new Set<ArtifactKind>([
  "file",
  "card",
  "draft",
  "packet",
  "form",
  "settings",
  "plan",
  "key_facts",
  "time",
  "company",
  "workflows"
]);

const isArtifact = (value: unknown): value is Artifact => {
  const a = value as Partial<Artifact> | null;
  return (
    !!a &&
    typeof a.id === "string" &&
    typeof a.kind === "string" &&
    KINDS.has(a.kind as ArtifactKind)
  );
};

export type OpenArtifactsState = {
  tabs: Artifact[];
  activeKey: string | null;
};

export const EMPTY_OPEN_ARTIFACTS: OpenArtifactsState = {
  tabs: [],
  activeKey: null
};

/** The active key names an open tab whenever there is one: else the last. */
const withActive = (
  tabs: Artifact[],
  activeKey: string | null
): OpenArtifactsState => {
  if (activeKey && tabs.some((t) => artifactKey(t) === activeKey))
    return { tabs, activeKey };
  const last = tabs[tabs.length - 1];
  return { tabs, activeKey: last ? artifactKey(last) : null };
};

export const WORKFLOWS_TAB_TITLE = "Case Automations";

/** Saved tab state, read back: tabs this build cannot open are dropped. */
/**
 * Saved tabs, minus any that name another case's insights (an earlier build
 * wrote one case's tabs under the next case's key). The workflows tab takes
 * its current title, whatever an earlier build saved it as.
 */
export function restoreOpen(
  saved: unknown,
  individualId?: string
): OpenArtifactsState {
  const s = saved as Partial<OpenArtifactsState> | null;
  if (!s || !Array.isArray(s.tabs)) return EMPTY_OPEN_ARTIFACTS;
  const tabs = s.tabs
    .filter(isArtifact)
    .filter(
      (t) => !individualId || t.kind !== "key_facts" || t.id === individualId
    )
    .map((t) =>
      t.kind === "workflows" ? { ...t, title: WORKFLOWS_TAB_TITLE } : t
    );
  return withActive(tabs, typeof s.activeKey === "string" ? s.activeKey : null);
}

export function openArtifact(
  state: OpenArtifactsState,
  artifact: Artifact
): OpenArtifactsState {
  const key = artifactKey(artifact);
  const existing = state.tabs.findIndex((t) => artifactKey(t) === key);
  if (existing >= 0) {
    // Re-opening refreshes title and page, keeps position.
    const tabs = state.tabs.slice();
    tabs[existing] = { ...tabs[existing], ...artifact };
    return { tabs, activeKey: key };
  }
  return { tabs: [...state.tabs, artifact], activeKey: key };
}

export function closeArtifact(
  state: OpenArtifactsState,
  key: string
): OpenArtifactsState {
  const index = state.tabs.findIndex((t) => artifactKey(t) === key);
  if (index < 0) return state;
  const tabs = state.tabs.filter((_, i) => i !== index);
  if (state.activeKey !== key) return { tabs, activeKey: state.activeKey };
  // Prefer the neighbour to the right, then left (VS Code behaviour).
  const next = tabs[index] ?? tabs[index - 1] ?? null;
  return { tabs, activeKey: next ? artifactKey(next) : null };
}

export function closeOthers(
  state: OpenArtifactsState,
  key: string
): OpenArtifactsState {
  const keep = state.tabs.find((t) => artifactKey(t) === key);
  return keep ? { tabs: [keep], activeKey: key } : state;
}

export function activate(
  state: OpenArtifactsState,
  key: string
): OpenArtifactsState {
  return state.tabs.some((t) => artifactKey(t) === key)
    ? { ...state, activeKey: key }
    : state;
}

export function retitle(
  state: OpenArtifactsState,
  key: string,
  title: string
): OpenArtifactsState {
  const index = state.tabs.findIndex((t) => artifactKey(t) === key);
  if (index < 0 || state.tabs[index].title === title) return state;
  const tabs = state.tabs.slice();
  tabs[index] = { ...tabs[index], title };
  return { tabs, activeKey: state.activeKey };
}

/** Drop tabs whose artifact no longer exists (e.g. a deleted file). */
export function pruneMissing(
  state: OpenArtifactsState,
  exists: (artifact: Artifact) => boolean
): OpenArtifactsState {
  const tabs = state.tabs.filter(exists);
  const next = withActive(tabs, state.activeKey);
  return tabs.length === state.tabs.length && next.activeKey === state.activeKey
    ? state
    : next;
}

export function activeArtifact(state: OpenArtifactsState): Artifact | null {
  return state.tabs.find((t) => artifactKey(t) === state.activeKey) ?? null;
}
